import React, { useEffect, useState } from 'react';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

import { useUser } from '../../../contexts/UserContext';
import { dmarcApi, fmtEpoch, fmtNumber, DmarcReport } from '../../../services/dmarcApi';
import SourceKindChip from './SourceKindChip';

const AlignmentChip: React.FC<{ value?: string }> = ({ value }) => {
  const v = (value || '').toLowerCase();
  if (v === 'pass') return <Chip size="small" label="pass" color="success" variant="outlined" />;
  if (v === 'fail') return <Chip size="small" label="fail" color="error" variant="outlined" />;
  return <Chip size="small" label={value || '—'} variant="outlined" />;
};

const DispositionChip: React.FC<{ value?: string }> = ({ value }) => {
  const v = (value || '').toLowerCase();
  if (v === 'none') return <Chip size="small" label="none" variant="outlined" />;
  if (v === 'quarantine')
    return <Chip size="small" label="quarantine" color="warning" variant="outlined" />;
  if (v === 'reject')
    return <Chip size="small" label="reject" color="error" variant="outlined" />;
  return <Chip size="small" label={value || '—'} variant="outlined" />;
};

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <Grid size={{ xs: 12, md: 6 }}>
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
    <Box>{children}</Box>
  </Grid>
);

const ReportDetail: React.FC = () => {
  const { isAdmin } = useUser();
  const navigate = useNavigate();
  const { domain: domainParam, reportUid: reportUidParam } = useParams<{
    domain: string;
    reportUid: string;
  }>();
  const domain = domainParam ? decodeURIComponent(domainParam) : '';
  const reportUid = reportUidParam ? decodeURIComponent(reportUidParam) : '';

  const [report, setReport] = useState<DmarcReport | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isAdmin()) navigate('/app/welcome');
  }, [isAdmin, navigate]);

  useEffect(() => {
    if (!isAdmin() || !domain || !reportUid) return;
    dmarcApi
      .getReport(domain, reportUid)
      .then(setReport)
      .catch(e => setError(e instanceof Error ? e.message : 'load failed'));
  }, [domain, reportUid, isAdmin]);

  if (!isAdmin()) return null;

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Button
        component={RouterLink}
        to={`/app/admin/dmarc/domains/${encodeURIComponent(domain)}`}
        startIcon={<ArrowBackIcon />}
        size="small"
        sx={{ mb: 2 }}
      >
        {domain}
      </Button>

      <Typography variant="h4" component="h1" fontWeight={600}>
        Report detail
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={3} sx={{ wordBreak: 'break-all' }}>
        {reportUid}
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>Failed to load: {error}</Alert>}
      {!report && !error && (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress size={28} />
        </Box>
      )}

      {report && (
        <>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" mb={2}>
                Summary
              </Typography>
              <Grid container spacing={2}>
                <Field label="Reporting org">
                  <Typography fontWeight={500}>{report.reporting_org}</Typography>
                </Field>
                <Field label="Report ID">
                  <Typography sx={{ fontFamily: 'monospace', fontSize: 12 }}>
                    {report.report_id}
                  </Typography>
                </Field>
                <Field label="Window">
                  <Typography>
                    {fmtEpoch(report.date_begin)} → {fmtEpoch(report.date_end)}
                  </Typography>
                </Field>
                <Field label="Processed at">
                  <Typography>{report.processed_at || '—'}</Typography>
                </Field>
                <Field label="Total messages">
                  <Typography variant="h5" fontWeight={700} sx={{ fontFeatureSettings: '"tnum"' }}>
                    {fmtNumber(report.total_messages)}
                  </Typography>
                </Field>
                <Field label="Pass DMARC">
                  <Typography variant="h5" fontWeight={700} sx={{ fontFeatureSettings: '"tnum"' }}>
                    {fmtNumber(report.pass_dmarc)}
                  </Typography>
                </Field>
              </Grid>
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <Typography variant="h6" mb={2}>
                Records
              </Typography>
              {report.records.length === 0 ? (
                <Typography color="text.secondary" textAlign="center" py={4}>
                  No records in this report
                </Typography>
              ) : (
                <TableContainer component={Paper} variant="outlined" sx={{ overflowX: 'auto' }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Source IP</TableCell>
                        <TableCell>Kind</TableCell>
                        <TableCell align="right">Count</TableCell>
                        <TableCell>Disposition</TableCell>
                        <TableCell>DKIM aligned</TableCell>
                        <TableCell>SPF aligned</TableCell>
                        <TableCell>Header.from</TableCell>
                        <TableCell>DKIM result</TableCell>
                        <TableCell>SPF result</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {report.records.map((rec, i) => {
                        const fail =
                          (rec.dkim_aligned || '').toLowerCase() !== 'pass' &&
                          (rec.spf_aligned || '').toLowerCase() !== 'pass';
                        return (
                          <TableRow
                            key={i}
                            sx={{ backgroundColor: fail ? 'rgba(255,0,0,0.04)' : undefined }}
                          >
                            <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>
                              {rec.source_ip}
                            </TableCell>
                            <TableCell>
                              <SourceKindChip kind={rec.source_kind} />
                            </TableCell>
                            <TableCell align="right">{fmtNumber(rec.count)}</TableCell>
                            <TableCell>
                              <DispositionChip value={rec.disposition} />
                            </TableCell>
                            <TableCell>
                              <AlignmentChip value={rec.dkim_aligned} />
                            </TableCell>
                            <TableCell>
                              <AlignmentChip value={rec.spf_aligned} />
                            </TableCell>
                            <TableCell sx={{ fontSize: 12 }}>{rec.header_from}</TableCell>
                            <TableCell sx={{ fontSize: 12, color: 'text.secondary' }}>
                              {rec.dkim_result || '—'}
                            </TableCell>
                            <TableCell sx={{ fontSize: 12, color: 'text.secondary' }}>
                              {rec.spf_result || '—'}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </Container>
  );
};

export default ReportDetail;
